/**
 * The role vocabulary these two surfaces read, and the one place it is written.
 *
 * **Roles are not configuration, and that is why they may be seeded here.** The
 * catalog's «Configuration, not features» table does not carry a roles row;
 * architecture §1.3 and §6 make role definitions **code-seeded and immutable at
 * runtime** — `auth__roles` is a migration plus a catalog edit, never a console
 * write — and there is no list endpoint to read them from: `backend-auth`
 * publishes `GET /api/v1/roles/{name}` for one role and no route for the set.
 * The UI/UX pack says the same thing in its own words: «the vocabulary comes
 * from the role registry declared in the architecture document, not from this
 * design».
 *
 * **One vocabulary, seven services.** `user` · `operator` · `admin` is what
 * `auth__roles` seeds and what every peer that takes a grant seeds too, which is
 * what lets one control write one name everywhere. There is no second registry
 * and no second control: `content` used to seed `operator_editorial_reviewer`
 * beside them, and this file used to offer a separate «مراجِع تحريريّ» toggle
 * that wrote `editorial_reviewer` — a name **no service has ever seeded**, so
 * the grant it offered could not succeed. `content`'s `operator` now carries
 * `editorial_review`, and the two-person rule is where it always was: the
 * self-review refusal on the post's author.
 *
 * `anonymous` is absent on purpose: it is the absence of a session rather than
 * an account, so it never occupies a row and no filter can return one.
 */

/** `auth__roles`, minus `anonymous`. The default is `user`, and it is sparse. */
export const ACCOUNT_ROLES = ['user', 'operator', 'admin'] as const;

export type AccountRoleName = (typeof ACCOUNT_ROLES)[number];

/**
 * The role facet's values — the same three, because there is nothing else to
 * filter by. The roster matches them against `auth__user_roles`.
 */
export const ROLE_FILTER_VALUES = ACCOUNT_ROLES;

/** True when the account carries a role, which is what makes a biography exist. */
export function holdsRole(role: string | undefined): boolean {
  return role !== undefined && role !== '' && role !== 'user';
}
